import { useState } from 'react';
import useSWR from 'swr';
import { AgencyContactEntity } from '@/entities/agency-contact-entity';
import { route } from 'nextjs-routes';
import FrontendHttpClient from '@/utils/http-handler/frontend-http-client';
import NotificationService from '@/components/notification-service';
import { useCurrentDivision } from '@/hooks/use-current-division';

export const useAgencyContact = () => {
  const [contacts, setContacts] = useState([]);
  const { currentDivision } = useCurrentDivision();
  const divisionNumber = currentDivision?.amsId;

  const { mutate, isValidating } = useSWR(
    route({
      pathname: '/api/v2/agency',
      query: { divisionNumber: divisionNumber ?? 'GLOBAL' },
    }),
    {
      onSuccess: data => {
        const contactData = data
          .sort(o => (o.isDefault ? -1 : 1))
          .map(o => new AgencyContactEntity(o));
        setContacts(contactData);
      },
    }
  );

  const update = async (id, newData) => {
    try {
      const res = await FrontendHttpClient.put(
        route({
          pathname: '/api/v2/agency/[id]',
          query: { id },
        }),
        newData
      );

      return res.data.result;
    } catch (err) {
      let message = 'Error updating Agency Contact';

      NotificationService.error(message);

      return null;
    }
  };

  const getDefault = async () => {
    try {
      const res = await FrontendHttpClient.get(
        route({
          pathname: '/api/v2/agency',
          query: { isDefault: true },
        })
      );
      if (res.data) {
        return res.data[0];
      }
      return null;
    } catch (err) {
      NotificationService.error('Error fetching default agency contact');
      return null;
    }
  };

  const setDefault = async newDefaultId => {
    const curDefault = await getDefault();
    if (curDefault && curDefault.id === newDefaultId) {
      return;
    }
    if (curDefault) {
      await update(curDefault.id, { isDefault: false });
    }

    await update(newDefaultId, { isDefault: true });
  };

  return { contacts, mutate, isValidating, update, getDefault, setDefault };
};
